import React from 'react';
import PropTypes from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';
import axios, { API } from 'config/axios';
import { withTranslation } from 'config/i18n';
import getConfig from 'next/config';
// Material UI Core
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import MuiLink from '@material-ui/core/Link';
import FormControlLabel from '@material-ui/core/FormControlLabel';
// React components
import TextField from 'components/shared/LightTextField';
import { useLayoutSnackbar, useLayoutState } from 'components/shared/Layout';
import useErrorHandler from 'utils/useErrorHandler';
// Material UI icons
import CloseIcon from '@material-ui/icons/Close';
import PrivacyPolicyDialog from './PrivacyPolicyDialog';

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '29.375rem',
    maxHeight: '90vh',
    backgroundColor: theme.palette.background.default,
    color: theme.palette.secondary.main,
    [theme.breakpoints.down('sm')]: {
      margin: theme.typography.pxToRem(25),
    },
  },
  dividers: {
    borderTop: `1px solid ${theme.palette.grey['50']}`,
    padding: theme.spacing(2, 1.5),
  },
  dialogContent: {
    padding: theme.spacing(2, 4, 6),
  },
  form: {
    '& > *': {
      margin: theme.spacing(1.3, 0),
    },
  },
  hint: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.grey['300'],
    marginBottom: 0,
  },
  recaptch: {
    display: 'flex',
    justifyContent: 'center',
    '@media (max-width: 400px)': {
      justifyContent: 'flex-start',
    },
    '& > div': {
      maxWidth: theme.typography.pxToRem(280),
      transform: 'scale(0.88)',
      transformOrigin: '0 0',
    },
  },
  checkbox: {
    color: 'rgba(0, 0, 0, 0.54)',
  },
  policyTypography: {
    fontSize: theme.typography.pxToRem(14),
    '& $policyLink': {
      fontWeight: 500,
      color: theme.palette.secondary.main,
      textDecoration: 'underline',
    },
  },
  policyLink: {},
  subscribe: {
    marginBottom: theme.spacing(2),
  },
}));

const NewsLetterDialog = (props) => {
  const classes = useStyles();
  const {
    onClose, open, t, ...other
  } = props;

  const recaptchaRef = React.useRef();

  const {
    publicRuntimeConfig: { envVars },
  } = getConfig();
  const { layoutState } = useLayoutState();

  const [privacyDialog, setPrivacyDialog] = React.useState(false); // Privacy policy dialog

  const [snackbar, setSnackbar] = useLayoutSnackbar();

  const [value, setValue] = React.useState({
    email: '',
    'g-recaptcha-response': '',
    privacyPolicy: false,
  });

  const handleInputChange = (e) => {
    setValue({
      ...value,
      [e.target.name]: e.target.value,
    });
  };

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!value.privacyPolicy) {
      setSnackbar({
        ...snackbar,
        open: true,
        severity: 'error',
        message: t('check_in_form:read_terms_and_conditions'),
      });
      return;
    }
    try {
      const { data } = await axios.post(API.newslettersSubscribe, {
        authenticity_token: layoutState.authenticity_token,
        'g-recaptcha-response': value['g-recaptcha-response'],
        newsletter_subscription: {
          email: value.email,
        },
      });
      window.ga('send', 'event', data.ga_tracking_event_category, 'Lead', 'Newsletter');
      window.gtag('event', 'newsletter_signup', {
        event_category: data.ga_tracking_event_category,
        event_label: 'Newsletter',
        event_action: 'Lead',
      });
      setSnackbar({
        ...snackbar,
        open: true,
        severity: 'success',
        message: data.message,
      });
      setValue({ email: '', 'g-recaptcha-response': '', privacyPolicy: false }); // Reset dialog
      handleClose();
    } catch (error) {
      let message = '';
      let isExpected = false; // Whether if error is expected or not
      if (error.response?.status && error.response.data) {
        switch (error.response.status) {
          case 406:
          case 403:
            if (error.response.data.message) {
              message = error.response.data.message;
            }
            isExpected = true;
            break;
        }
      }
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      if (message) {
        setSnackbar({
          ...snackbar,
          open: true,
          severity: 'error',
          message,
        });
      }
      if (!isExpected) {
        useErrorHandler(error, t, [snackbar, setSnackbar]);
      }
    }
  };

  const onRecaptchChange = (token) => {
    setValue({
      ...value,
      'g-recaptcha-response': token,
    });
  };

  return (
    <>
      <Dialog
        id="newsletter-dialog"
        aria-labelledby="countries-dialog"
        open={open}
        classes={{
          paper: classes.paper,
        }}
        maxWidth="md"
        keepMounted
        onClose={handleClose}
        {...other}
      >
        <DialogTitle>
          <Typography variant="h4">
            <b>EagleShare Newsletter</b>
          </Typography>
          <IconButton
            onClick={handleClose}
            aria-label="Cancel"
            color="secondary"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          className={classes.dialogContent}
          classes={{
            dividers: classes.dividers,
          }}
        >
          <form onSubmit={handleSubmit} className={classes.form}>
            <Typography variant="body2" className={classes.hint}>
              {t('layout:footer.receive_deals')}
            </Typography>
            <TextField
              color="secondary"
              label={t('layout:footer.email_address')}
              type="email"
              name="email"
              variant="outlined"
              fullWidth
              value={value.email}
              onChange={handleInputChange}
            />
            {envVars.RECAPTCHA_SITE_KEY && (
              <div className={classes.recaptch}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={envVars.RECAPTCHA_SITE_KEY}
                  onChange={onRecaptchChange}
                />
              </div>
            )}
            <FormControlLabel
              value="end"
              control={(
                <Checkbox
                  edge="start"
                  name="privacyPolicy"
                  color="primary"
                  classes={{ root: classes.checkbox }}
                  checked={value.privacyPolicy}
                  onChange={(e) => setValue({
                    ...value,
                    privacyPolicy: e.target.checked,
                  })}
                />
              )}
              label={(
                <Typography className={classes.policyTypography}>
                  {t('layout:footer.read_agreed')}
                  &nbsp;
                  <MuiLink
                    href="https://www.eaglerider.com/privacy-policy"
                    color="primary"
                    target="_blank"
                    className={classes.policyLink}
                  >
                    {t('layout:footer.er_privacy_policy')}
                  </MuiLink>
                  {' '}
                  and
                  {' '}
                  <MuiLink
                    href="https://support.eaglerider.com/eagleshare-privacy-policy"
                    color="primary"
                    target="_blank"
                    className={classes.policyLink}
                  >
                    EagleShare Privacy Policy
                  </MuiLink>
                </Typography>
              )}
              labelPlacement="end"
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              className={classes.subscribe}
            >
              {t('layout:footer.subscribe')}
            </Button>
            <Typography variant="body2" className={classes.hint}>
              {t('layout:footer.subscribe_txt')}
            </Typography>
          </form>
        </DialogContent>
      </Dialog>
      <PrivacyPolicyDialog
        open={privacyDialog}
        onClose={() => setPrivacyDialog(false)}
      />
    </>
  );
};

NewsLetterDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default withTranslation(['layout', 'check_in_form'])(
  NewsLetterDialog,
);
